import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { itemService } from "../../api/services/itemService";
import { addToCart } from "../../utils/cartUtils";
import LoginRequiredModal from "../LoginRequiredModal/LoginRequiredModal";
import Loader from "../Loader/Loader";
import defaultProductImage from "../../images/Default-Image.jpeg";
import TruncatedTitle from "../TruncatedTitle/TruncatedTitle";
import { useCanViewPrices } from "../../utils/pricingVisibility";
import "./ProductDetailInfo.css";
import "../CategoryPage/CategoryPage.css";

const ProductDetailInfo = () => {
  const showPrices = useCanViewPrices();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Product Detail State
  const [productDetail, setProductDetail] = useState({
    id: null,
    title: "",
    price: 0,
    originalPrice: 0,
    sku: "",
    binNumber: "",
    image: "",
    images: [],
    availability: "In Stock",
    description: "",
    availableQty: 0,
    weight: 0,
    weightLb: 0,
    weightOz: 0,
    length: 0,
    width: 0,
    height: 0,
  });
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [zoomState, setZoomState] = useState({
    active: false,
    xPercent: 50,
    yPercent: 50,
  });
  const mainImageRef = useRef(null);

  // Related Products State
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [allRelatedProducts, setAllRelatedProducts] = useState([]);
  const [displayedCount, setDisplayedCount] = useState(4); // Number of items currently displayed
  const [hasMoreProducts, setHasMoreProducts] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentApiPage, setCurrentApiPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [, setIsInitialLoad] = useState(true);
  const [newlyLoadedIds, setNewlyLoadedIds] = useState([]); // Track newly loaded product IDs for animation
  const itemsPerPage = 4;

  // Quantity state for main product
  const [quantity, setQuantity] = useState(0);
  const [relatedQuantities, setRelatedQuantities] = useState({});
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);

  // Helper function to get full image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return "";
    // If it's already a full URL, return as is
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }
    // If it's a relative path starting with /, prepend the image base URL
    if (imagePath.startsWith("/")) {
      return `https://wireox.erp.gold${imagePath}`;
    }
    return imagePath;
  };

  // Update displayed products based on current count
  const updateDisplayedProducts = (
    allProducts,
    count,
    total,
    previousCount = 0,
  ) => {
    const toShow = allProducts.slice(0, count);
    setRelatedProducts(toShow);

    // Track newly loaded products for fade-in animation
    if (count > previousCount) {
      const newlyLoaded = toShow.slice(previousCount, count).map((p) => p.id);
      setNewlyLoadedIds(newlyLoaded);
      // Clear animation class after animation completes
      setTimeout(() => {
        setNewlyLoadedIds([]);
      }, 600); // Match animation duration
    }

    // Check if we need to fetch more from API or show more from cache
    const needsMoreFromApi =
      count >= allProducts.length && allProducts.length < total;
    const hasMoreInCache = count < allProducts.length;
    setHasMoreProducts(needsMoreFromApi || hasMoreInCache);
  };

  // API function to fetch product detail and related items
  const fetchProductDetailAndRelatedItems = useCallback(
    async (itemId, pageNumber = 1) => {
      if (pageNumber === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);
      try {
        const response = await itemService.getItemDetailAndRelatedItems(
          itemId,
          pageNumber,
          itemsPerPage,
        );

        if (response && response.Item) {
          // Transform main product data (only on first load)
          if (pageNumber === 1) {
            const item = response.Item;
            const rawImages = Array.isArray(item.Images) ? item.Images : [];
            const imageUrls = rawImages
              .map((img) => {
                const path =
                  typeof img === "object" && img != null ? img.ImagePath : img;
                return path ? getImageUrl(path) : "";
              })
              .filter(Boolean);
            const mainImage = imageUrls[0] || "";

            setProductDetail({
              id: item.ItemId,
              title: item.ItemName || "",
              price:
                item.DiscountPrice > 0
                  ? item.DiscountPrice
                  : item.UnitPrice || 0,
              originalPrice:
                item.DiscountPrice > 0 && item.UnitPrice > item.DiscountPrice
                  ? item.UnitPrice
                  : item.UnitPrice || 0,
              sku: item.SKU || "",
              binNumber: item.PartNumber || "",
              image: mainImage || defaultProductImage,
              images: imageUrls,
              availability: item.AvailableQty > 0 ? "In Stock" : "Out of Stock",
              description: item.ItemDescription || "",
              availableQty: item.AvailableQty || 0,
              weight: item.Weight ?? 0,
              weightLb: item.WeightLb ?? 0,
              weightOz: item.WeightOz ?? 0,
              length: item.Length ?? 0,
              width: item.Width ?? 0,
              height: item.Height ?? 0,
            });
          }

          // Transform related products
          if (response.RelatedItems && response.RelatedItems.Items) {
            const transformedRelated = response.RelatedItems.Items.map(
              (relatedItem) => {
                const firstRelatedImg =
                  relatedItem.Images && relatedItem.Images.length > 0
                    ? relatedItem.Images[0]
                    : null;
                const relatedImage = firstRelatedImg
                  ? getImageUrl(
                      typeof firstRelatedImg === "object"
                        ? firstRelatedImg.ImagePath
                        : firstRelatedImg,
                    )
                  : "";

                return {
                  id: relatedItem.ItemId,
                  name: relatedItem.ItemName || "",
                  price:
                    relatedItem.DiscountPrice > 0
                      ? relatedItem.DiscountPrice
                      : relatedItem.UnitPrice || 0,
                  originalPrice:
                    relatedItem.DiscountPrice > 0 &&
                    relatedItem.UnitPrice > relatedItem.DiscountPrice
                      ? relatedItem.UnitPrice
                      : relatedItem.UnitPrice || 0,
                  badge: relatedItem.Brand
                    ? `GENUINE ${relatedItem.Brand}`
                    : "",
                  image: relatedImage || defaultProductImage,
                  core: "Without Core",
                  availableQty: relatedItem.AvailableQty || 0,
                };
              },
            );

            const pagination = response.RelatedItems.Pagination || {};
            const total = pagination.TotalCount || 0;
            setTotalCount(total);

            if (pageNumber === 1) {
              setAllRelatedProducts(transformedRelated);
              const initialDisplay = transformedRelated.slice(0, itemsPerPage);
              setRelatedProducts(initialDisplay);
              setDisplayedCount(initialDisplay.length);
              setCurrentApiPage(1);
              setIsInitialLoad(false);
              setNewlyLoadedIds([]);
              setHasMoreProducts(
                initialDisplay.length < total ||
                  transformedRelated.length < total,
              );
            } else {
              setAllRelatedProducts((prev) => {
                const updated = [...prev, ...transformedRelated];
                setCurrentApiPage(pageNumber);
                setDisplayedCount((prevDisplayed) => {
                  const newDisplayedCount = Math.min(
                    prevDisplayed + itemsPerPage,
                    updated.length,
                  );
                  updateDisplayedProducts(
                    updated,
                    newDisplayedCount,
                    total,
                    prevDisplayed,
                  );
                  return newDisplayedCount;
                });
                return updated;
              });
            }
          }
        }
      } catch (err) {
        console.error("Error fetching product detail:", err);
        setError("Failed to load product details. Please try again.");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [itemsPerPage],
  );

  // Scroll to top when component mounts or ItemID changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [searchParams]);

  useEffect(() => {
    setActiveImageIndex(0);
  }, [searchParams]);

  // Load product data from API
  useEffect(() => {
    const itemId = searchParams.get("ItemID");
    if (itemId) {
      setCurrentApiPage(1);
      setDisplayedCount(4);
      setAllRelatedProducts([]);
      setRelatedProducts([]);
      setHasMoreProducts(false);
      setTotalCount(0);
      setIsInitialLoad(true); // Reset initial load flag
      fetchProductDetailAndRelatedItems(parseInt(itemId), 1);
    }
  }, [searchParams, fetchProductDetailAndRelatedItems]);

  // Load more products (show next 4 from cache or fetch from API)
  const loadMoreProducts = () => {
    const itemId = searchParams.get("ItemID");
    if (!itemId || loadingMore) return;

    const newCount = displayedCount + itemsPerPage;
    const previousCount = displayedCount;

    // If we have enough items in cache, just show more
    if (newCount <= allRelatedProducts.length) {
      setDisplayedCount(newCount);
      updateDisplayedProducts(
        allRelatedProducts,
        newCount,
        totalCount,
        previousCount,
      );
    }
    // If we need more items from API
    else if (allRelatedProducts.length < totalCount) {
      const nextApiPage = currentApiPage + 1;
      fetchProductDetailAndRelatedItems(parseInt(itemId, 10), nextApiPage);
    }
  };

  // Event handlers
  const handleQuantityChange = (change) => {
    setQuantity((prev) => {
      const availableQty = productDetail.availableQty || 0;
      const newQuantity = prev + change;
      // Don't allow quantity to go below 0 or above availableQty
      if (newQuantity < 0) return prev;
      if (newQuantity > availableQty) return prev;
      return newQuantity;
    });
  };

  const handleQuantityInputChange = (e) => {
    const inputValue = parseInt(e.target.value) || 0;
    const availableQty = productDetail.availableQty || 0;
    // Limit input value to 0 and availableQty
    const newQuantity = Math.max(0, Math.min(inputValue, availableQty));
    setQuantity(newQuantity);
  };

  const handleAddToCart = () => {
    if (productDetail.id && quantity > 0) {
      const result = addToCart(
        {
          id: productDetail.id,
          name: productDetail.title,
          price: productDetail.price,
          quantity: quantity,
          image:
            (productDetail.images && productDetail.images[0]) ||
            productDetail.image,
        },
        () => setShowLoginModal(true),
      );
      // If login is not required and item was added, reset quantity
      if (result) {
        setQuantity(0);
      }
    }
  };

  const handleRelatedProductClick = (productId) => {
    // Navigate to product detail page for the related product
    window.location.href = `/product-detail?ItemID=${productId}`;
  };

  const handleRelatedQuantityChange = (productId, change) => {
    const product = relatedProducts.find((p) => p.id === productId);
    if (!product) return;

    const availableQty = product.availableQty || 0;
    const currentQty = relatedQuantities[productId] || 0;
    const nextQty = currentQty + change;
    if (nextQty < 0) return;
    if (nextQty > availableQty) return;

    setRelatedQuantities((prev) => ({
      ...prev,
      [productId]: nextQty,
    }));
  };

  const handleRelatedAddToCart = (productId) => {
    const product = relatedProducts.find((p) => p.id === productId);
    const qty = relatedQuantities[productId] || 0;
    if (!product || qty <= 0) return;

    const result = addToCart(
      {
        id: product.id,
        name: product.name,
        price: product.price,
        quantity: qty,
        image: product.image,
      },
      () => setShowLoginModal(true),
    );

    if (result) {
      setRelatedQuantities((prev) => ({
        ...prev,
        [productId]: 0,
      }));
    }
  };
  const mainProductImageSrc = React.useMemo(() => {
    const imgs = productDetail.images;
    if (Array.isArray(imgs) && imgs.length > 0) {
      const idx = Math.min(activeImageIndex, imgs.length - 1);
      return imgs[idx] || defaultProductImage;
    }
    return productDetail.image || defaultProductImage;
  }, [productDetail.images, productDetail.image, activeImageIndex]);

  const descriptionWords = React.useMemo(() => {
    if (!productDetail.description) return [];
    return productDetail.description.trim().split(/\s+/).filter(Boolean);
  }, [productDetail.description]);
  const isDescriptionLong = descriptionWords.length > 100;
  const displayedDescription =
    isDescriptionLong && !showFullDescription
      ? `${descriptionWords.slice(0, 100).join(" ")}...`
      : productDetail.description;

  const formatSpecValue = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    const num = Number(value);
    return Number.isNaN(num) ? value : num;
  };

  const productSpecifications = React.useMemo(() => {
    const specRows = [
      {
        label: "Weight",
        rawValue: productDetail.weight,
        displayValue: formatSpecValue(productDetail.weight),
      },
      {
        label: "Weight (LB)",
        rawValue: productDetail.weightLb,
        displayValue: `${formatSpecValue(productDetail.weightLb)} lb`,
      },
      {
        label: "Weight (OZ)",
        rawValue: productDetail.weightOz,
        displayValue: `${formatSpecValue(productDetail.weightOz)} oz`,
      },
      {
        label: "Length",
        rawValue: productDetail.length,
        displayValue: `${formatSpecValue(productDetail.length)} in`,
      },
      {
        label: "Width",
        rawValue: productDetail.width,
        displayValue: `${formatSpecValue(productDetail.width)} in`,
      },
      {
        label: "Height",
        rawValue: productDetail.height,
        displayValue: `${formatSpecValue(productDetail.height)} in`,
      },
    ];

    return specRows
      .filter((spec) => Number(spec.rawValue) !== 0)
      .map((spec) => ({
        label: spec.label,
        value: spec.displayValue,
      }));
  }, [productDetail]);

  useEffect(() => {
    setZoomState((prev) => ({ ...prev, active: false }));
  }, [mainProductImageSrc]);

  useEffect(() => {
    setShowFullDescription(false);
  }, [productDetail.id]);

  const handleMainImageMouseEnter = () => {
    setZoomState((prev) => ({ ...prev, active: true }));
  };

  const handleMainImageMouseLeave = () => {
    setZoomState((prev) => ({ ...prev, active: false }));
  };

  const handleMainImageMouseMove = (e) => {
    const box = mainImageRef.current;
    if (!box) return;
    const rect = box.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const clamp = (v) => Math.max(0, Math.min(100, v));

    setZoomState({
      active: true,
      xPercent: clamp(x),
      yPercent: clamp(y),
    });
  };

  return (
    <>
      {/* Login Required Modal */}
      <LoginRequiredModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />
      {/* Loader Overlay */}
      {loading && <Loader />}

      {/* Main Content Area - Product Detail Info */}
      <main className="product-detail-info-main">
        {error && (
          <div style={{ textAlign: "center", padding: "2rem", color: "red" }}>
            <p>{error}</p>
          </div>
        )}
        {!loading && !error && productDetail.id && (
          <>
            <div className="product-detail-container">
              {/* Product Image Section */}
              <div className="product-image-section">
                <div className="product-image-gallery">
                  <div className="product-image-wrapper">
                    {/* OEM Badge */}
                    {/* <div className="oem-badge">
                <div className="oem-badge-content">
                  <span className="oem-text">OEM</span>
                  <span className="grade-text">GRADE A</span>
                </div>
              </div> */}

                    {/* Menu Icon */}
                    {/* <div className="product-menu-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                </svg>
              </div> */}

                    {/* Main Product Image */}
                    <div className="main-product-image">
                      <img
                        src={mainProductImageSrc}
                        alt={productDetail.title}
                        className="product-image"
                        ref={mainImageRef}
                        onMouseEnter={handleMainImageMouseEnter}
                        onMouseMove={handleMainImageMouseMove}
                        onMouseLeave={handleMainImageMouseLeave}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = defaultProductImage;
                        }}
                      />
                    </div>
                    {zoomState.active ? (
                      <div
                        className="product-image-zoom-preview"
                        aria-hidden="true"
                        style={{
                          backgroundImage: `url("${mainProductImageSrc}")`,
                          backgroundPosition: `${zoomState.xPercent}% ${zoomState.yPercent}%`,
                        }}
                      />
                    ) : null}

                    {productDetail.images && productDetail.images.length > 1 ? (
                      <div
                        className="product-image-thumbnails"
                        role="list"
                        aria-label="Product images"
                      >
                        {productDetail.images.map((url, idx) => (
                          <button
                            key={`${url}-${idx}`}
                            type="button"
                            role="listitem"
                            className={
                              idx === activeImageIndex
                                ? "product-image-thumb product-image-thumb--active"
                                : "product-image-thumb"
                            }
                            onClick={() => setActiveImageIndex(idx)}
                            aria-label={`View image ${idx + 1}`}
                            aria-current={idx === activeImageIndex}
                          >
                            <img
                              src={url || defaultProductImage}
                              alt=""
                              loading="lazy"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = defaultProductImage;
                              }}
                            />
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* Product Details Section */}
              <div className="product-details-section">
                {/* Availability Badge */}
                <div className="availability-badge">
                  <span
                    className={
                      productDetail.availability === "In Stock"
                        ? "in-stock"
                        : "out-of-stock"
                    }
                  >
                    {productDetail.availability}
                  </span>
                </div>

                {/* Product Title */}
                <h1 className="product-title">{productDetail.title}</h1>

                {/* SKU and Bin Number */}
                <div className="product-identifiers">
                  {productDetail.sku && (
                    <div className="sku-container">
                      <div className="sku-badge">
                        <span className="sku-label">SKU</span>
                        <span className="sku-value">{productDetail.sku}</span>
                        <button
                          className="copy-icon"
                          onClick={() => {
                            navigator.clipboard.writeText(productDetail.sku);
                          }}
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <rect
                              x="9"
                              y="9"
                              width="13"
                              height="13"
                              rx="2"
                              ry="2"
                            ></rect>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                          </svg>
                        </button>
                      </div>
                      <div className="copy-sku-label">Copy SKU</div>
                    </div>
                  )}

                  {productDetail.binNumber && (
                    <div className="bin-number-badge">
                      <span className="bin-label">New Bin Number</span>
                      <span className="bin-value">
                        {productDetail.binNumber}
                      </span>
                    </div>
                  )}
                </div>

                {/* Price — hidden for guests */}
                {showPrices && (
                  <div className="product-price">
                    <span className="price-amount">
                      ${productDetail.price.toFixed(2)}
                    </span>
                    {productDetail.originalPrice > productDetail.price && (
                      <span
                        className="original-price"
                        style={{
                          marginLeft: "10px",
                          textDecoration: "line-through",
                          color: "#999",
                        }}
                      >
                        ${productDetail.originalPrice.toFixed(2)}
                      </span>
                    )}
                  </div>
                )}

                {/* Quantity and Add to Cart */}
                <div className="product-detail-purchase-section">
                  {productDetail.availableQty > 0 ? (
                    <>
                      <div className="product-detail-quantity-selector">
                        <button
                          className="product-detail-quantity-btn product-detail-minus-btn"
                          onClick={() => handleQuantityChange(-1)}
                          disabled={quantity <= 0}
                        >
                          -
                        </button>
                        <input
                          type="number"
                          className="product-detail-quantity-input"
                          value={quantity}
                          min="0"
                          max={productDetail.availableQty || 0}
                          onChange={handleQuantityInputChange}
                        />
                        <button
                          className="product-detail-quantity-btn product-detail-plus-btn"
                          onClick={() => handleQuantityChange(1)}
                          disabled={
                            quantity >= (productDetail.availableQty || 0)
                          }
                        >
                          +
                        </button>
                      </div>
                      <button
                        className="product-detail-add-to-cart-btn"
                        onClick={handleAddToCart}
                      >
                        Add to Cart
                      </button>
                    </>
                  ) : (
                    <button
                      className="product-detail-add-to-cart-btn product-detail-out-of-stock-btn"
                      disabled
                    >
                      Out of Stock
                    </button>
                  )}
                </div>

                {productDetail.description && (
                  <div className="product-detail-description-section">
                    <h3 className="product-detail-description-title">
                      Description
                    </h3>
                    <p className="product-detail-description-text">
                      {displayedDescription}
                    </p>
                    {isDescriptionLong && (
                      <button
                        type="button"
                        className="product-detail-description-toggle-btn"
                        onClick={() =>
                          setShowFullDescription((prevState) => !prevState)
                        }
                      >
                        {showFullDescription ? "Show less" : "Show more"}
                      </button>
                    )}
                  </div>
                )}

                {productSpecifications.length > 0 && (
                  <div className="product-detail-specifications-section">
                    <h3 className="product-detail-specifications-title">
                      Specifications
                    </h3>
                    <table className="product-detail-specifications-table">
                      <tbody>
                        {productSpecifications.map((spec) => (
                          <tr key={spec.label}>
                            <th scope="row">{spec.label}</th>
                            <td>{spec.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Service Guarantees Section */}
                <div className="product-detail-service-guarantees">
                  <div className="product-detail-guarantee-item">
                    <div className="product-detail-guarantee-icon">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M16 3h5v5M21 3l-7 7M8 21H3v-5M3 21l7-7" />
                        <path d="M3 12a9 9 0 0 1 9-9 9 9 0 0 1 9 9 9 9 0 0 1-9 9 9 9 0 0 1-9-9z" />
                      </svg>
                    </div>
                    <span className="product-detail-guarantee-text">
                      Same Day Ship
                    </span>
                  </div>

                  <div className="product-detail-guarantee-item">
                    <div className="product-detail-guarantee-icon">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <rect
                          x="1"
                          y="4"
                          width="22"
                          height="16"
                          rx="2"
                          ry="2"
                        />
                        <line x1="1" y1="10" x2="23" y2="10" />
                      </svg>
                    </div>
                    <span className="product-detail-guarantee-text">
                      Secure Payment
                    </span>
                  </div>

                  <div className="product-detail-guarantee-item">
                    <div className="product-detail-guarantee-icon">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M3 12a9 9 0 0 1 9-9 9 9 0 0 1 9 9 9 9 0 0 1-9 9 9 9 0 0 1-9-9z" />
                        <path d="M9 12l2 2 4-4" />
                      </svg>
                    </div>
                    <span className="product-detail-guarantee-text">
                      Easy refunds & Returns
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Related Products Section */}
            <div className="product-detail-related-container">
              <div className="product-detail-related-header">
                <h2 className="product-detail-related-title">
                  RELATED PRODUCTS
                </h2>
              </div>
            </div>
            {/* Related Products Grid - Using CategoryPage Structure */}
            <div className="wirerox-products-section">
              <div className="cps-products-row">
                {relatedProducts.map((product) => {
                  const qty = relatedQuantities[product.id] || 0;
                  const availableQty = product.availableQty || 0;

                  return (
                    <div
                      key={product.id}
                      className={`cps-product-card ${newlyLoadedIds.includes(product.id) ? "fade-in-product" : ""}`}
                    >
                      <div
                        className="cps-logo-container"
                        onClick={() => handleRelatedProductClick(product.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleRelatedProductClick(product.id);
                          }
                        }}
                      >
                        <img
                          src={product.image || defaultProductImage}
                          alt={product.name}
                          className={`cps-product-logo-img ${(product.image || defaultProductImage) === defaultProductImage ? "cps-product-logo-img--default" : ""}`}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = defaultProductImage;
                            e.target.classList.add(
                              "cps-product-logo-img--default",
                            );
                          }}
                        />
                      </div>

                      <TruncatedTitle
                        className="cps-title"
                        onClick={() => handleRelatedProductClick(product.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleRelatedProductClick(product.id);
                          }
                        }}
                      >
                        {product.name}
                      </TruncatedTitle>

                      {showPrices && (
                        <div className="cps-price-section">
                          <span className="cps-current-price">
                            ${product.price.toFixed(2)}
                          </span>
                          {product.originalPrice > product.price ? (
                            <span className="cps-old-price">
                              ${product.originalPrice.toFixed(2)}
                            </span>
                          ) : null}
                        </div>
                      )}

                      <span className="cps-sub-text">{product.core}</span>

                      {availableQty > 0 ? (
                        <>
                          <div className="cps-qty-selector">
                            <button
                              type="button"
                              className="cps-qty-btn"
                              onClick={() =>
                                handleRelatedQuantityChange(product.id, -1)
                              }
                              disabled={qty <= 0}
                              aria-label="Decrease quantity"
                            >
                              -
                            </button>
                            <div className="cps-qty-input">{qty}</div>
                            <button
                              type="button"
                              className="cps-qty-btn"
                              onClick={() =>
                                handleRelatedQuantityChange(product.id, 1)
                              }
                              disabled={qty >= availableQty}
                              aria-label="Increase quantity"
                            >
                              +
                            </button>
                          </div>

                          <button
                            type="button"
                            className="cps-add-cart-btn"
                            onClick={() => handleRelatedAddToCart(product.id)}
                            disabled={qty <= 0}
                          >
                            Add To Cart
                          </button>
                        </>
                      ) : (
                        <div className="cps-out-of-stock">Out of Stock</div>
                      )}
                    </div>
                  );
                })}
              </div>
              {loadingMore && (
                <div
                  style={{
                    textAlign: "center",
                    marginTop: "30px",
                    marginBottom: "30px",
                  }}
                >
                  <Loader />
                </div>
              )}
              {hasMoreProducts && !loadingMore && (
                <div style={{ textAlign: "center", marginTop: "30px" }}>
                  <button
                    onClick={loadMoreProducts}
                    disabled={loadingMore}
                    style={{
                      padding: "12px 30px",
                      background: "#1a331c",
                      color: "white",
                      border: "none",
                      borderRadius: "25px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "700",
                      transition: "all 0.3s ease",
                    }}
                  >
                    Load More Products
                  </button>
                </div>
              )}
            </div>
          </>
        )}
        {!loading && !error && !productDetail.id && (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <p>No product selected. Please select a product to view details.</p>
          </div>
        )}
      </main>
    </>
  );
};

export default ProductDetailInfo;
